import React, { useEffect, useState } from "react";
import {
    Grid,
    Typography,
    Button,
    TextField,
    CircularProgress
} from "@material-ui/core";
import { useStyles } from "./RefundDetails.styles";
import { Warning, Done } from '@material-ui/icons';
import Alert from "@material-ui/lab/Alert";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";

import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import RefundTransactionService from "../../../services/RefundTransactionService";
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import { useConfirmationDialog } from "../../../hooks/useConfirmationDialog";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import apiClient from "../../../auth/apiClient";
import CashieredDeviceSummaryReceipt, {
    ReceiptType, ReceiptContentType
} from "../../CashierDevice/SummaryReceipt/CashieredDeviceSummaryReceipt.jsx";

const refundTransactionService = new RefundTransactionService(apiClient);

const Label = React.memo(({ title, classes }) => {
    return (
        <Typography color="primary" className={(classes.centered, classes.labelTitle)}>
            {title}
        </Typography>
    );
});
Label.displayName = "Label";

const schema = yup.object().shape({
    refundReason: yup.string().trim().required("Required"),
});

const RefundDetails = ({ parkingTransactionId, onCancel }) => {
    const classes = useStyles();
    const { facilityID: entityID } = useCurrentFacility();
    const { showDialog } = useConfirmationDialog();
    const enqueueSnackbar = useEnqueueSnackbar();

    const [refundDetails, setRefundDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dialogType, setDialogType] = useState(null);

    const REFUND_STATUS = {
        IDLE: 0,
        PROCESSING: 1,
        SUCCESS: 2,
        FAILED: 3
    };

    const [refundStatus, setRefundStatus] = useState(REFUND_STATUS.IDLE);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { refundReason: "" },
        mode: "onChange",
    });

    const handleDialogOpen = (type) => {
        setDialogType(type);
    };

    const handleDialogClose = () => {
        setDialogType(null);
    };

    const getRefundDetails = async () => {
        setIsLoading(true);
        try {
            const response =
                await refundTransactionService.searchRefundTransactions({
                    entityID,
                    transactionID: parkingTransactionId,
                    limit: 1,
                    offset: 0,
                });
            const refundData = response?.data?.collection?.[0];
            if (refundData != null) {
                setRefundDetails(refundData);
            }
        } catch {
            enqueueSnackbar("Failed to fetch refund details", {
                variant: "error",
                tag: "FailedToFetchRefundDetails",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (parkingTransactionId)
            getRefundDetails();
    }, [parkingTransactionId]);

    const submitRefund = async (formData) => {
        const { refundReason } = formData;

        const confirm = await showDialog({
            title: "Refund Confirmation",
            message: `Are you sure you want to refund payment on ticket ${refundDetails.ticketNumber}?`,
            buttons: [
                { name: "Yes", color: "primary" },
                { name: "No", color: "secondary" },
            ],
        });

        if (confirm === "No") return;

        try {
            setRefundStatus(REFUND_STATUS.PROCESSING);
            const response = await refundTransactionService.requestRefund({
                entityID,
                paymentsTenderedID: refundDetails.paymentsTenderedID,
                amount: refundDetails.amount,
                refundReason
            });
            const isRefundSuccess = response?.data?.isRefundSuccessful;
            setRefundStatus(isRefundSuccess ? REFUND_STATUS.SUCCESS : REFUND_STATUS.FAILED);

        } catch {
            setRefundStatus(REFUND_STATUS.FAILED);
        }
    };

    const handleCancel = () => {
        onCancel();
    };

    return (
        <Grid
            container
            className={classes.refundContainer}
        >
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    {refundDetails && (
                        <Grid
                            container
                            direction="column"
                            spacing={3}
                            key={refundDetails.ticketNumber}
                            className={classes.innerContainer}
                        >
                            <Grid item >
                                <Grid item>
                                    <Typography variant="h5" className={classes.title}>
                                        Refund Details
                                    </Typography>
                                </Grid>

                                <Grid item>
                                    <Label title="Ticket" classes={classes} />
                                    <Typography className={classes.value}>{refundDetails.ticketNumber}</Typography>
                                </Grid>

                                <Grid item>
                                    <Label title="Customer" classes={classes} />
                                    <Typography className={classes.value}>
                                        {[refundDetails.customerFirstName, refundDetails.customerLastName].filter(Boolean).join(" ")}
                                    </Typography>
                                </Grid>

                                <Grid item>
                                    <Label title="Amount" classes={classes} />
                                    <Typography className={classes.value}>
                                        ${refundDetails.amount?.toFixed(2) ?? "0.00"}
                                    </Typography>
                                </Grid>

                                <Grid item>
                                    <Controller
                                        name="refundReason"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                id="refund-reason"
                                                variant="outlined"
                                                required
                                                fullWidth
                                                label="Refund Reason"
                                                disabled={
                                                    refundStatus === REFUND_STATUS.SUCCESS ||
                                                    refundStatus === REFUND_STATUS.PROCESSING
                                                }
                                                error={!!errors.refundReason}
                                                helperText={
                                                    errors.refundReason?.message || " "
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item>
                                {refundStatus === REFUND_STATUS.IDLE && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.submitButton}
                                        onClick={handleSubmit(submitRefund)}
                                    >
                                        SUBMIT REFUND
                                    </Button>
                                )}

                                {refundStatus === REFUND_STATUS.PROCESSING && (
                                    <Grid item xs={12} className={classes.centerAlign}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled
                                            className={classes.submitButton}
                                            startIcon={<CircularProgress size={18} />}
                                        >
                                            Processing...
                                        </Button>
                                    </Grid>
                                )}

                                {refundStatus === REFUND_STATUS.SUCCESS && (
                                    <Grid container direction="column" spacing={2}>
                                        <Grid item>
                                            <Button
                                                disabled
                                                fullWidth
                                                className={classes.refundSuccessful}
                                                startIcon={<Done />}
                                            >
                                                Refund Successful
                                            </Button>
                                        </Grid>

                                        <Grid item>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() =>
                                                    handleDialogOpen(ReceiptType.SMS)
                                                }
                                            >
                                                Text Receipt
                                            </Button>
                                        </Grid>

                                        <Grid item>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() =>
                                                    handleDialogOpen(ReceiptType.EMAIL)
                                                }
                                            >
                                                Email Receipt
                                            </Button>
                                        </Grid>

                                        <Grid item>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={handleCancel}
                                            >
                                                Close
                                            </Button>
                                        </Grid>
                                    </Grid>
                                )}

                                {refundStatus === REFUND_STATUS.FAILED && (
                                    <Grid item xs={12} spacing={1}>
                                        <Grid container direction="column" >

                                            <Grid item xs={12}>
                                                <Alert
                                                    severity="warning"
                                                    icon={<Warning />}
                                                    className={classes.warningAlert}
                                                >
                                                    Payment could not be refunded.
                                                </Alert>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <div className={classes.buttonRow}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={handleCancel}
                                                        className={classes.buttonRight}
                                                    >
                                                        Cancel
                                                    </Button>

                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        className={classes.buttonRight}
                                                        onClick={handleSubmit(submitRefund)}
                                                    >
                                                        Try Again
                                                    </Button>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </>
            )}

            {dialogType && (
                <Dialog
                    open={!!dialogType}
                    onClose={handleDialogClose}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogContent>
                        <CashieredDeviceSummaryReceipt
                            receiptType={dialogType}
                            onCancel={handleDialogClose}
                            onReceiptSent={handleDialogClose}
                            classes={classes}
                            ticketNumber={refundDetails.ticketNumber}
                            credentialID={refundDetails.credentialID}
                            receiptContentType={ReceiptContentType.REFUND}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Grid>
    );
};

export default RefundDetails;